import type { SupportedLocale } from "@/lib/i18n";
import type { Product } from "@/data/products";

/**
 * SEO-oriented product type labels (search keywords), not UI category filters.
 */
export type SeoProductCategory =
  | "tank"
  | "tankDestroyer"
  | "howitzer"
  | "antiAircraft"
  | "ifv"
  | "ship"
  | "engine"
  | "vehicle"
  | "crane"
  | "robot"
  | "set";

type LocaleLabels = Partial<Record<SupportedLocale, string>> & { en: string };

export const SEO_PRODUCT_CATEGORY_LABELS: Record<SeoProductCategory, LocaleLabels> = {
  tank: {
    en: "Tank",
    pl: "czołg",
    de: "Panzer",
    fr: "char",
    es: "tanque",
    nl: "tank",
    zh: "坦克",
  },
  tankDestroyer: {
    en: "Tank Destroyer",
    pl: "niszczyciel czołgów",
    de: "Jagdpanzer",
    fr: "chasseur de chars",
    es: "cazacarros",
    nl: "tankjager",
    zh: "坦克歼击车",
  },
  howitzer: {
    en: "Howitzer",
    pl: "haubica",
    de: "Haubitze",
    fr: "obusier",
    es: "obús",
    nl: "houwitser",
    zh: "榴弹炮",
  },
  antiAircraft: {
    en: "Anti-Aircraft Vehicle",
    pl: "samobieżne działo przeciwlotnicze",
    de: "Flugabwehrpanzer",
    fr: "véhicule antiaérien",
    es: "vehículo antiaéreo",
    nl: "luchtafweervoertuig",
    zh: "防空车辆",
  },
  ifv: {
    en: "Infantry Fighting Vehicle",
    pl: "bojowy wóz piechoty",
    de: "Schützenpanzer",
    fr: "véhicule de combat d'infanterie",
    es: "vehículo de combate de infantería",
    nl: "infanteriegevechtsvoertuig",
    zh: "步兵战车",
  },
  ship: {
    en: "Ship",
    pl: "okręt",
    de: "Schiff",
    fr: "navire",
    es: "barco",
    nl: "schip",
    zh: "军舰",
  },
  engine: {
    en: "Engine",
    pl: "silnik",
    de: "Motor",
    fr: "moteur",
    es: "motor",
    nl: "motor",
    zh: "发动机",
  },
  vehicle: {
    en: "Vehicle",
    pl: "pojazd",
    de: "Fahrzeug",
    fr: "véhicule",
    es: "vehículo",
    nl: "voertuig",
    zh: "载具",
  },
  crane: {
    en: "Crane",
    pl: "dźwig",
    de: "Kran",
    fr: "grue",
    es: "grúa",
    nl: "kraan",
    zh: "起重机",
  },
  robot: {
    en: "Robot",
    pl: "robot",
    de: "Roboter",
    fr: "robot",
    es: "robot",
    nl: "robot",
    zh: "机器人",
  },
  set: {
    en: "Building Set",
    pl: "zestaw klocków",
    de: "Bausatz",
    fr: "set de construction",
    es: "set de construcción",
    nl: "bouwset",
    zh: "积木套装",
  },
};

/** Explicit overrides where name heuristics are ambiguous. */
const SEO_CATEGORY_BY_PRODUCT_ID: Partial<Record<string, SeoProductCategory>> = {
  "97017": "tankDestroyer", // Waffentrager E-100
  "97018": "tankDestroyer", // Jagdpanzer E-100
  "97021": "tankDestroyer",
  "97022": "tankDestroyer",
  "97023": "tankDestroyer",
  "97026": "howitzer", // Karl Gerat
  "97031": "antiAircraft", // Wirbelwind
  "97037": "crane",
  "97038": "tankDestroyer", // FV4005
  "97056": "tankDestroyer", // FV215
  "97059": "tank", // Panther II
  "97060": "tankDestroyer", // Jagdtiger
  "97066": "tankDestroyer", // Sturmgeschütz IV / StuG IV
  "95008": "engine",
  "95009": "engine",
  "95010": "engine",
  "95011": "engine",
  "95023": "set", // Garage modular building
  "96005": "tank", // Abrams-X
  "96006": "ifv", // Stryker X
  "96007": "ifv", // M2 Bradley
  "96008": "ifv", // ZBL-19
  "96009": "robot", // Unmanned Mechanical Dog
  "96010": "antiAircraft", // 625E Air Defense
  "96011": "ifv", // KF-41 Lynx
  "91013": "vehicle", // Lynx Tactical ATV
};

function inferSeoCategory(product: Product): SeoProductCategory {
  const name = (product.name ?? "").toLowerCase();

  if (product.category === "civilian") return "vehicle";
  if (product.militarySubcategory === "ships") return "ship";

  if (/engine/.test(name)) return "engine";
  if (/crane|gantry/.test(name)) return "crane";
  if (/dog|robot/.test(name)) return "robot";
  if (
    /jagd|elefant|elephant|ferdinand|fv4005|jagdtiger|waffentrager|tank destroyer/.test(
      name
    )
  ) {
    return "tankDestroyer";
  }
  if (/karl|howitzer|gerät|gerat/.test(name)) return "howitzer";
  if (/wirbelwind|flak|air defense|antiaircraft|anti-aircraft/.test(name)) {
    return "antiAircraft";
  }
  if (/bradley|stryker|lynx|zbl|ifv|infantry/.test(name)) return "ifv";
  if (/tank|panzer|tiger|maus|sherman|pershing|armata|leopard|centurion|strv|kv-|is-2|t-14|t34|type 99|type 100|abrams/.test(name)) {
    return "tank";
  }
  if (product.militarySubcategory === "tanks_vehicles") return "tank";
  if (product.militarySubcategory === "other") return "set";

  return "set";
}

export function resolveSeoProductCategory(product: Product): SeoProductCategory {
  if (product.seoCategory) return product.seoCategory;
  const byId = SEO_CATEGORY_BY_PRODUCT_ID[product.id];
  if (byId) return byId;
  return inferSeoCategory(product);
}

export function getSeoCategoryLabel(
  category: SeoProductCategory,
  locale: SupportedLocale
): string {
  return SEO_PRODUCT_CATEGORY_LABELS[category][locale] ?? SEO_PRODUCT_CATEGORY_LABELS[category].en;
}

export function getProductSeoScale(product: Product): string {
  const scale = product.scale?.trim();
  if (!scale || scale.toLowerCase() === "other") return "";
  return scale;
}
